import { Controller } from "@hotwired/stimulus";
import { debounce } from "lodash";
import compact from "lodash/compact";
import {
  parseTagParams,
  formatTagValueWithOperator,
} from "app/components/analytics/shared/tagHelpers";

// Connects with data-controller="tests-filter-dialog"
export default class extends Controller<HTMLElement> {
  static targets = [
    "tagKey",
    "tagValue",
    "operator",
    "pill",
    "emptyState",
    "filterType",
    "tagKeysList",
    "pane",
  ];

  declare tagKeyTarget: HTMLInputElement;
  declare tagValueTarget: HTMLInputElement;
  declare tagValueTargets: HTMLInputElement[];
  declare operatorTarget: HTMLSelectElement;
  declare pillTargets: HTMLElement[];
  declare hasPillTargets: boolean;
  declare tagKeys: string[];
  declare emptyStateTarget: HTMLElement;
  declare tagKeysListTarget: HTMLElement;
  declare hasEmptyStateTarget: boolean;
  declare filterTypeTarget: HTMLSelectElement;
  declare hasFilterTypeTarget: boolean;
  declare paneTargets: HTMLElement[];

  initialize(): void {
    this.tagKeys = [];
  }

  pillTargetConnected(element) {
    this.tagKeys.push(element.dataset.key);

    element.addEventListener("click", this.tagKeyChange.bind(this));
  }

  submit(event) {
    event.preventDefault(); // Prevent default form submission

    const submitBtn = event.target;
    submitBtn.value = "Add filter…";
    submitBtn.disabled = true;

    const form = this.element as HTMLFormElement;
    const url = new URL(form.action);
    const params = new URLSearchParams(url.search);

    const selectedPaneTagValue =
      this.tagValueTargets.find((target) => !target.disabled)?.value || "";

    const filterType = this.filterTypeTarget.value;

    if (filterType === "labels" || filterType === "owners") {
      const queryParams = params.get(filterType);
      const currentArray = queryParams?.split(",").map((value) => value.trim()) ?? [];
      const newArray = compact(Array.from(new Set([...currentArray, selectedPaneTagValue])));
      params.set(filterType, newArray.join(","));
    } else if (filterType === "tags") {
      const tags = parseTagParams(params.get("tags"));
      const tagValue = formatTagValueWithOperator(selectedPaneTagValue, this.operatorTarget.value);
      const newTags = tags.includeByKey(this.tagKeyTarget.value, tagValue);

      params.set(filterType, newTags.toQueryString());
    } else {
      // If the filter type is not tag, label, or owner, then we just override the existing param
      params.set(filterType, selectedPaneTagValue);
    }

    url.search = params.toString();

    const turboFrame = this.element.closest("turbo-frame[data-executions]");

    if (turboFrame) {
      turboFrame.setAttribute("src", url.toString());
    } else {
      // If the turbo frame is not found, we can use Turbo to
      // visit the URL directly.
      Turbo.visit(url.toString());
    }
  }

  tagKeyChange(event) {
    event.preventDefault();
    this.tagKeyTarget.value = event.target.dataset.key;
  }

  searchTagKeys({ target: { value: searchInput } }) {
    debounce(() => {
      this.filterTagKeys(searchInput);
    }, 300)();
  }

  filterTagKeys(searchInput) {
    if (this.hasPillTargets) {
      return;
    }

    const searchedKeys = this.tagKeys.filter((key) => key.includes(searchInput));

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = searchedKeys.length !== 0;
    }

    this.pillTargets.forEach((element) => {
      const key = element.dataset.key;

      if (key) {
        element.hidden = !searchedKeys.includes(key);
      }
    });
  }

  updateFilterType() {
    const filterType = this.filterTypeTarget?.value;

    this.paneTargets.forEach((pane) => {
      if (pane.dataset.key === filterType) {
        pane.classList.remove("hidden");

        // Enable any inputs not in the current pane
        pane.querySelectorAll("input").forEach((input) => {
          input.disabled = false;
        });
      } else {
        pane.classList.add("hidden");

        // Disable any inputs not in the current pane
        pane.querySelectorAll("input").forEach((input) => {
          input.disabled = true;
        });
      }
    });
  }
}
